import { z } from 'zod';
import { AdviserRole } from './domain.js';
import { adviserIdSchema, adviserRoleSchema, isoInstantSchema, passwordPolicySchema, } from './schemas.js';
/**
 * Team-management contracts: a firm OWNER invites people into their own firm by
 * a one-time sign-up link, and the invitee accepts it to create their own login.
 *
 * Two trust boundaries shape these schemas:
 *  - The OWNER-only management endpoints (`/api/v1/team*`) never accept `firmId`
 *    or `createdBy` — those come from the verified session + RLS context.
 *  - The PUBLIC accept endpoints (`/api/v1/auth/invite*`) take only the opaque
 *    token (+ a chosen password). The firm, email and role are read from the
 *    invite server-side — a token can only ever join the firm that issued it.
 *
 * `.strict()` everywhere: an auth/admin payload must never carry extra fields.
 */
// --- invitable roles --------------------------------------------------------
/**
 * The roles an Owner may hand out via an invite link. A second OWNER is
 * deliberately NOT invitable — owner is a stronger privilege than should be
 * grantable through an emailed link, so it is excluded at the contract edge
 * (a 422, never a silent downgrade). Widening this is a reviewed product
 * decision, not a toggle.
 */
export const invitableRoleSchema = z.enum([
    AdviserRole.Adviser,
    AdviserRole.Paraplanner,
    AdviserRole.ReadOnly,
]);
/** Lifecycle of an invite, computed server-side from the row's timestamps. */
export const inviteStatusSchema = z.enum(['pending', 'accepted', 'revoked', 'expired']);
// --- create invite (Owner) --------------------------------------------------
/**
 * What an Owner submits to invite a colleague. `firmId`/`createdBy` are NEVER
 * accepted here — they come from the session + RLS context.
 */
export const teamInviteCreateRequestSchema = z
    .object({
    email: z.string().trim().toLowerCase().email(),
    role: invitableRoleSchema,
})
    .strict();
/**
 * The response to creating an invite. `token` is the RAW single-use token —
 * returned exactly ONCE, at creation, so the Owner can build the link
 * (`<origin>/accept-invite/<token>`); only its hash is ever stored. It never
 * appears on any read afterwards.
 */
/**
 * How the invite email fared (GOAL 2026-09-10 item 5). `not_configured` is the
 * honest state of an environment with no email provider: the owner copies the
 * link, as before. `failed` carries the provider's sentence and is recoverable
 * with a resend. The status is on the row, never only in a log.
 */
export const inviteDeliverySchema = z
    .object({
    status: z.enum(['not_sent', 'sent', 'failed', 'not_configured']),
    error: z.string().nullable(),
    lastSentAt: isoInstantSchema.nullable(),
    sendCount: z.number().int().min(0),
})
    .strict();
export const teamInviteCreatedSchema = z
    .object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: invitableRoleSchema,
    token: z.string().min(1),
    expiresAt: isoInstantSchema,
    delivery: inviteDeliverySchema,
})
    .strict();
// --- read: overview (Owner) -------------------------------------------------
/** A pending/spent invite as shown on the Team page. Never carries the token. */
export const teamInviteReadSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    role: invitableRoleSchema,
    status: inviteStatusSchema,
    expiresAt: isoInstantSchema,
    createdAt: isoInstantSchema,
    delivery: inviteDeliverySchema,
});
/** An active member of the firm, as shown on the Team page. */
export const teamMemberSchema = z.object({
    adviserId: adviserIdSchema,
    email: z.string().email(),
    displayName: z.string(),
    role: adviserRoleSchema,
    status: z.enum(['active', 'suspended', 'deactivated']),
    createdAt: isoInstantSchema,
});
/** The Team page payload: current members + outstanding invites. */
export const teamOverviewResponseSchema = z.object({
    members: z.array(teamMemberSchema),
    invites: z.array(teamInviteReadSchema),
});
// --- public: validate an invite ---------------------------------------------
/**
 * Preview request. The token travels in the POST body (never a URL/query) so it
 * cannot land in an access log — same log-hygiene reason accept is a POST.
 */
export const inviteValidateRequestSchema = z
    .object({
    token: z.string().min(1),
})
    .strict();
/**
 * What the public accept page shows before the invitee commits: who the invite
 * is for and which firm they'd join. Returned only for a LIVE token; an
 * expired/revoked/accepted/unknown token gets a generic invalid response
 * (no oracle) rather than this shape.
 */
export const inviteValidateResponseSchema = z
    .object({
    email: z.string().email(),
    firmName: z.string(),
    role: invitableRoleSchema,
})
    .strict();
// --- public: accept an invite -----------------------------------------------
/**
 * Password rule mirroring the Cognito pool policy (MinimumLength 8; upper +
 * lower + number + symbol) so a weak password fails as a friendly field-level
 * 422 at the edge rather than round-tripping to Cognito as an opaque 500. The
 * server still surfaces Cognito's own InvalidPasswordException as a 422 too, in
 * case the pool policy tightens ahead of this schema.
 */
/** The one password rule (see `passwordPolicySchema`); kept under its historical name. */
export const invitePasswordSchema = passwordPolicySchema;
/**
 * Accept an invite: the opaque token from the link + the invitee's chosen
 * password. Email/role/firm are read from the invite server-side, never the
 * body. The response reuses `authLoginResponseSchema` (`mfa_setup`) so the
 * existing MFA-enrolment screen drives first-login TOTP with no new UI.
 */
export const inviteAcceptRequestSchema = z
    .object({
    token: z.string().min(1),
    password: invitePasswordSchema,
})
    .strict();
// --- member lifecycle (Owner) ----------------------------------------------
/**
 * GOAL 2026-09-10 item 4 — access revocation through the existing lifecycle
 * convention: an owner suspends a member (status → `suspended`; the database
 * refuses their next query regardless of any live token, 0011's
 * app_adviser_active()) and reinstates them (→ `active`). Deactivation and
 * the reassign-all step it needs stay a later slice (ADMIN_RUNBOOK § 6.5).
 */
export const teamMemberStatusUpdateSchema = z
    .object({
    status: z.enum(['active', 'suspended']),
})
    .strict();
//# sourceMappingURL=team.js.map